import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Image, Upload, X, Edit2, Save } from "lucide-react";
import { API } from "@/App";
import { toast } from "sonner";

const AdminGalleryPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    category: "restaurant",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const categories = [
    { id: "restaurant", label: "Restaurant" },
    { id: "food", label: "Mat" },
    { id: "events", label: "Arrangementer" },
    { id: "team", label: "Team" },
  ];

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error("Error fetching gallery:", error);
      toast.error("Kunne ikke laste galleri");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("Bildet er for stort. Maks 10MB");
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({ title: "", category: "restaurant" });
    setImageFile(null);
    setImagePreview(null);
    setShowUpload(false);
    setEditingImage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingImage && !imageFile) {
      toast.error("Velg et bilde å laste opp");
      return;
    }

    setUploading(true);

    try {
      if (editingImage) {
        await axios.delete(`${API}/admin/gallery/${editingImage.id}`);
        toast.success("Bilde slettet!");
      } else {
        const uploadData = new FormData();
        uploadData.append("image", imageFile);

        await axios.post(`${API}/admin/gallery`, uploadData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Bilde lastet opp!");
      }

      resetForm();
      fetchGallery();
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Kunne ikke laste opp bilde");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Er du sikker på at du vil slette dette bildet?")) return;

    try {
      await axios.delete(`${API}/admin/gallery/${imageId}`);
      toast.success("Bilde slettet!");
      fetchGallery();
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Kunne ikke slette bilde");
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title,
      category: image.category,
    });
    setImagePreview(`${API.replace('/api', '')}${image.image_url}`);
    setShowUpload(true);
  };

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Header */}
      <div 
        className="py-12 mandala-pattern"
        style={{ background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="indian-divider mb-2"
              >
                <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Admin</span>
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl sm:text-4xl font-serif-display font-semibold italic"
                style={{ color: '#d4af37' }}
                data-testid="admin-gallery-title"
              >
                Galleribehandling
              </motion.h1>
            </div>
            <Button 
              onClick={() => { resetForm(); setShowUpload(true); }}
              className="rounded-full btn-gold"
              data-testid="upload-image-button"
            >
              <Upload className="h-5 w-5 mr-2" />
              Last opp Bilde
            </Button>
          </div>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upload Modal */}
        {showUpload && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(45, 24, 16, 0.9)' }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl p-8 w-full max-w-lg"
              style={{ border: '3px solid #d4af37' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-serif-display font-semibold" style={{ color: '#2d1810' }}>
                  {editingImage ? "Rediger Bilde" : "Last opp Nytt Bilde"}
                </h2>
                <Button variant="ghost" onClick={resetForm} style={{ color: '#721111' }}>
                  <X className="h-6 w-6" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Image Upload/Preview */}
                <div className="text-center">
                  <div 
                    className={`relative w-full h-56 mx-auto rounded-xl overflow-hidden ${!editingImage ? 'cursor-pointer' : ''} group`}
                    style={{ background: '#ede1c8', border: '2px dashed #b27f47' }}
                    onClick={() => !editingImage && fileInputRef.current?.click()}
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full">
                        <Image className="h-16 w-16 mb-3" style={{ color: '#b27f47' }} />
                        <span style={{ color: '#8b4513' }}>Klikk for å velge bilde</span>
                        <span className="text-sm mt-1" style={{ color: '#a68e6e' }}>JPG, PNG, WEBP (maks 10MB)</span>
                      </div>
                    )}
                    {!editingImage && imagePreview && (
                      <div 
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: 'rgba(45, 24, 16, 0.7)' }}
                      >
                        <span className="text-white font-semibold">Klikk for å endre</span>
                      </div>
                    )}
                  </div>
                  {!editingImage && (
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  )}
                </div>

                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 rounded-full"
                    style={{ background: 'transparent', border: '2px solid #721111', color: '#721111' }}
                  >
                    Avbryt
                  </Button>
                  <Button
                    type="submit"
                    disabled={uploading}
                    className="flex-1 btn-maroon rounded-full transition-transform hover:scale-105"
                  >
                    <Save className="h-5 w-5 mr-2" />
                    {uploading ? "Laster opp..." : editingImage ? "Oppdater" : "Last opp"}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}

        {/* Gallery Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div 
              className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-r-transparent"
              style={{ borderColor: '#721111', borderRightColor: 'transparent' }}
            ></div>
            <p className="mt-4" style={{ color: '#4a3728' }}>Laster galleri...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl" style={{ border: '2px solid #b27f47' }}>
            <Image className="h-16 w-16 mx-auto mb-4" style={{ color: '#b27f47' }} />
            <p className="text-lg" style={{ color: '#4a3728' }}>Ingen bilder i galleriet ennå</p>
            <Button 
              onClick={() => setShowUpload(true)}
              className="mt-4 btn-maroon rounded-full"
            >
              <Plus className="h-5 w-5 mr-2" />
              Last opp Første Bilde
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative rounded-xl overflow-hidden shadow-lg"
                  style={{ border: '3px solid #8b4513' }}
                  data-testid={`gallery-admin-image-${image.id}`}
                >
                  <div className="aspect-square">
                    <img
                      src={`${API.replace('/api', '')}${image.image_url}`}
                      alt="Gallery"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div 
                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: 'rgba(45, 24, 16, 0.7)' }}
                  >
                    <Button
                      size="sm"
                      onClick={() => handleDelete(image.id)}
                      className="rounded-full"
                      style={{ background: '#ef4444', color: 'white' }}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Slett
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminGalleryPage;
